import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, BanknoteArrowDown, BanknoteArrowUp, Truck, Building2, Landmark, UserRound, MessageSquare, Mail } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Counter } from "../components/pt/Counter";
import { PentominoMark } from "../components/pt/PentominoMark";
import { useDocumentTitle } from "../lib/useDocumentTitle";

const ease = [0.22, 1, 0.36, 1];

const Home = () => {
    const { t, waUrl } = useLang();
    useDocumentTitle("/");

    return (
        <div data-testid="home-page">
            {/* ============== HERO ============== */}
            <section
                className="on-dark relative overflow-hidden bg-tinta pt-grain text-white"
                data-testid="hero-section"
            >
                {/* Hero image, right-aligned, soft fade into tinta */}
                <div className="absolute inset-0 z-0">
                    <img
                        src="/generated/hero-atm.png"
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 h-full w-full object-cover object-right opacity-55"
                        onError={(e) => {
                            e.currentTarget.style.display = "none";
                        }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-tinta via-tinta/92 to-tinta/35" />
                </div>

                <div className="wrap relative z-10 grid min-h-[640px] grid-cols-1 items-center gap-12 py-20 lg:min-h-[720px] lg:grid-cols-[1.15fr_0.85fr] lg:py-28">
                    <div>
                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.55, ease, delay: 0.05 }}
                            className="pt-eyebrow mb-6"
                            data-testid="hero-eyebrow"
                        >
                            <span className="dot" />
                            <span>{t.hero.eyebrow}</span>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease, delay: 0.15 }}
                            className="h1 max-w-[18ch] text-white"
                            data-testid="hero-title"
                        >
                            {t.hero.titleA}{" "}
                            <span className="relative inline-block">
                                <span className="relative z-10 text-coral">
                                    {t.hero.titleB}
                                </span>
                            </span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease, delay: 0.3 }}
                            className="lead mt-6 max-w-[58ch] text-[#C5CDD8]"
                            data-testid="hero-lead"
                        >
                            {t.hero.lead}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease, delay: 0.45 }}
                            className="mt-10 flex flex-wrap gap-3"
                        >
                            <a
                                href={waUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="pt-btn pt-pulse"
                                data-testid="hero-cta-whatsapp"
                            >
                                <MessageSquare size={18} />
                                {t.hero.ctaMain}
                            </a>
                            <a
                                href="#how"
                                className="pt-btn alt"
                                data-testid="hero-cta-how"
                            >
                                {t.hero.ctaAlt}
                                <ArrowDown size={16} />
                            </a>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, ease, delay: 0.65 }}
                            className="mt-14 flex items-center gap-3 text-xs text-[#8A95AB]"
                        >
                            <span className="inline-block h-px w-10 bg-[#2B3B5C]" />
                            <span className="uppercase tracking-widest">
                                {t.hero.scrollHint}
                            </span>
                        </motion.div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.92 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                        className="relative flex justify-center lg:justify-end"
                    >
                        <div className="relative">
                            <PentominoMark size={360} animate />
                            {/* floating stat card */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 1.1, duration: 0.6 }}
                                className="absolute -left-10 -bottom-6 hidden rounded-xl bg-white p-4 text-tinta shadow-2xl md:block"
                                data-testid="hero-floating-stat"
                            >
                                <div className="text-xs font-semibold uppercase tracking-wider text-acero">
                                    Junio 2025 · Operación real
                                </div>
                                <div
                                    className="mt-1 text-2xl font-semibold tab-nums"
                                    style={{ fontStretch: "118%" }}
                                >
                                    1.95× <span className="text-sm font-normal text-acero">entró por peso entregado</span>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ============== KPIs ============== */}
            <section className="sec" data-testid="kpis-section">
                <div className="wrap">
                    <div className="mb-14 grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-end">
                        <div>
                            <div className="pt-eyebrow mb-4">
                                <span className="dot" />
                                <span>Cifras verificables</span>
                            </div>
                            <h2 className="h2 max-w-[18ch]">{t.kpis.title}</h2>
                        </div>
                        <p className="lead">{t.kpis.lead}</p>
                    </div>

                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                        {t.kpis.items.map((item, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.4 }}
                                transition={{ duration: 0.55, delay: i * 0.08 }}
                                className="group relative overflow-hidden rounded-xl border border-linea bg-white p-7 transition-all hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(20,35,64,0.3)]"
                                data-testid={`kpi-card-${i}`}
                            >
                                <div className="absolute left-0 top-0 h-1 w-10 bg-coral transition-all group-hover:w-full group-hover:opacity-30" />
                                <div
                                    className="text-5xl font-semibold text-tinta"
                                    style={{ fontStretch: "118%", fontWeight: 640 }}
                                >
                                    <Counter
                                        to={item.value}
                                        prefix={item.prefix || ""}
                                        suffix={item.suffix || ""}
                                        decimals={item.decimals || 0}
                                        customDisplay={item.customDisplay || null}
                                        testId={`kpi-value-${i}`}
                                    />
                                </div>
                                <div className="mt-4 text-base font-semibold leading-tight text-tinta">
                                    {item.label}
                                </div>
                                <p className="mt-2 text-sm text-acero">
                                    {item.detail}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                    <p className="mt-8 text-sm text-acero">{t.kpis.footnote}</p>
                </div>
            </section>

            {/* ============== HOW IT WORKS ============== */}
            <section id="how" className="sec mist" data-testid="how-section">
                <div className="wrap">
                    <div className="mb-14 max-w-3xl">
                        <div className="pt-eyebrow mb-4">
                            <span className="dot" />
                            <span>{t.how.eyebrow}</span>
                        </div>
                        <h2 className="h2">{t.how.title}</h2>
                        <p className="lead">{t.how.lead}</p>
                    </div>

                    <div className="grid gap-6 md:grid-cols-3">
                        {t.how.steps.map((step, i) => {
                            const icons = [BanknoteArrowDown, BanknoteArrowUp, Truck];
                            const Icon = icons[i];
                            return (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.55, delay: i * 0.12 }}
                                    className="relative rounded-xl bg-white p-8 shadow-[0_1px_0_var(--pt-linea)]"
                                    data-testid={`how-step-${i}`}
                                >
                                    <div className="mb-6 flex items-center justify-between">
                                        <span
                                            className="text-sm font-semibold text-coral-profundo tab-nums"
                                            style={{ fontStretch: "118%" }}
                                        >
                                            {step.n}
                                        </span>
                                        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-tinta text-white">
                                            <Icon size={22} />
                                        </div>
                                    </div>
                                    <h3 className="h3 mb-3">{step.title}</h3>
                                    <p className="text-[0.98rem] text-acero">
                                        {step.body}
                                    </p>
                                    {i < 2 && (
                                        <div className="absolute right-[-18px] top-1/2 hidden h-px w-9 bg-linea md:block" />
                                    )}
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ============== SERVICES ============== */}
            <section className="sec" data-testid="services-section">
                <div className="wrap">
                    <div className="mb-14 grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-end">
                        <div>
                            <div className="pt-eyebrow mb-4">
                                <span className="dot" />
                                <span>{t.services.eyebrow}</span>
                            </div>
                            <h2 className="h2 max-w-[22ch]">
                                {t.services.title}
                            </h2>
                        </div>
                        <p className="lead">{t.services.lead}</p>
                    </div>

                    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                        {t.services.items.map((s, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                                className="group relative overflow-hidden rounded-xl border border-linea bg-white p-7 transition-all hover:border-tinta/40 hover:shadow-[0_30px_60px_-32px_rgba(20,35,64,0.3)]"
                                data-testid={`service-card-${i}`}
                            >
                                <div className="mb-5 flex items-center gap-3">
                                    <div className="h-9 w-1.5 rounded-sm bg-coral transition-all group-hover:h-11" />
                                    <span
                                        className="text-xs font-bold uppercase tracking-[0.14em] text-acero"
                                    >
                                        {s.tag}
                                    </span>
                                </div>
                                <h3
                                    className="h3 mb-3"
                                    style={{ fontStretch: "112%", fontSize: "1.35rem" }}
                                >
                                    {s.title}
                                </h3>
                                <p className="text-[0.98rem] text-acero">{s.body}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============== OPERATION / RECEIPT ============== */}
            <section className="sec mist" data-testid="operation-section">
                <div className="wrap grid items-center gap-14 lg:grid-cols-2">
                    <div>
                        <div className="pt-eyebrow mb-4">
                            <span className="dot" />
                            <span>{t.operation.eyebrow}</span>
                        </div>
                        <h2 className="h2 max-w-[22ch]">{t.operation.title}</h2>
                        <p className="lead">{t.operation.lead}</p>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.7 }}
                        className="justify-self-end"
                    >
                        <div className="pt-receipt" data-testid="operation-receipt">
                            <div className="h">{t.operation.receipt.title}</div>
                            {t.operation.receipt.rows.map(([k, v]) => (
                                <div className="pt-row" key={k}>
                                    <span className="lbl">{k}</span>
                                    <span className="dots" />
                                    <span className="val">{v}</span>
                                </div>
                            ))}
                            <p className="src">{t.operation.receipt.source}</p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ============== AUDIENCES ============== */}
            <section className="sec" data-testid="audiences-section">
                <div className="wrap">
                    <div className="mb-14 max-w-3xl">
                        <div className="pt-eyebrow mb-4">
                            <span className="dot" />
                            <span>{t.audiences.eyebrow}</span>
                        </div>
                        <h2 className="h2">{t.audiences.title}</h2>
                        <p className="lead">{t.audiences.lead}</p>
                    </div>

                    <div className="grid gap-6 lg:grid-cols-3">
                        {t.audiences.items.map((a, i) => {
                            const icons = [Building2, Landmark, UserRound];
                            const Icon = icons[i];
                            // Build piece marker pattern per audience
                            const patterns = {
                                "1": [["", ""], ["", ""], ["on", "gap"]],
                                "4": [["", "on"], ["", ""], ["", "gap"]],
                                "3": [["on", ""], ["", ""], ["", "gap"]],
                            };
                            const pattern = patterns[a.piece] || patterns["1"];
                            return (
                                <motion.article
                                    key={i}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.3 }}
                                    transition={{ duration: 0.55, delay: i * 0.1 }}
                                    className="group relative flex flex-col justify-between overflow-hidden rounded-2xl bg-tinta p-8 text-white transition-all hover:-translate-y-1 hover:shadow-[0_40px_80px_-30px_rgba(20,35,64,0.6)]"
                                    data-testid={`audience-card-${i}`}
                                >
                                    <div>
                                        <div className="mb-6 flex items-center justify-between">
                                            <span className="piece-marker" aria-hidden="true">
                                                {pattern.flat().map((cls, idx) => (
                                                    <i key={idx} className={cls} />
                                                ))}
                                            </span>
                                            <Icon size={22} className="text-coral" />
                                        </div>
                                        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#C5CDD8]">
                                            {a.tag}
                                        </div>
                                        <h3
                                            className="mb-5 text-3xl text-white"
                                            style={{ fontStretch: "118%", fontWeight: 640, lineHeight: 1.05 }}
                                        >
                                            {a.title}
                                        </h3>
                                        <ul className="space-y-3 text-[0.98rem] text-[#C5CDD8]">
                                            {a.bullets.map((b, j) => (
                                                <li
                                                    key={j}
                                                    className="flex items-start gap-3"
                                                >
                                                    <span className="mt-[9px] inline-block h-1.5 w-1.5 flex-none bg-coral" />
                                                    {b}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <Link
                                        to={
                                            i === 0
                                                ? "/comercios"
                                                : i === 1
                                                    ? "/bancos"
                                                    : "/personas"
                                        }
                                        className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-coral hover:text-white"
                                        data-testid={`audience-link-${i}`}
                                    >
                                        {a.cta}
                                        <ArrowRight size={16} />
                                    </Link>
                                </motion.article>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ============== CLOSING CTA ============== */}
            <section className="sec ink pt-grain relative overflow-hidden" data-testid="closing-section">
                <div className="absolute inset-0 opacity-25">
                    <img
                        src="/generated/hero-commerce.png"
                        alt=""
                        aria-hidden="true"
                        className="h-full w-full object-cover"
                        onError={(e) => {
                            e.currentTarget.style.display = "none";
                        }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-tinta/40 via-tinta/80 to-tinta" />
                </div>
                <div className="wrap relative z-10 text-center">
                    <div className="pt-eyebrow mb-5 justify-center">
                        <span className="dot" />
                        <span>{t.closing.eyebrow}</span>
                    </div>
                    <h2 className="h2 mx-auto max-w-[18ch] text-white">
                        {t.closing.title}
                    </h2>
                    <p className="lead mx-auto mt-4 max-w-[52ch] text-[#C5CDD8]">
                        {t.closing.lead}
                    </p>
                    <div className="mt-10 flex flex-wrap justify-center gap-3">
                        <a
                            href={waUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="pt-btn"
                            data-testid="closing-cta-whatsapp"
                        >
                            <MessageSquare size={18} />
                            {t.closing.ctaMain}
                        </a>
                        <Link
                            to="/contacto"
                            className="pt-btn alt"
                            data-testid="closing-cta-email"
                        >
                            <Mail size={16} />
                            {t.closing.ctaAlt}
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
